import * as React from "react";
import { SingleNavMenuLink, toLinkButton, toLinkDivider, toLinkPage, toLinkSpace } from "../identity/useNavMenu";
import { formSet, useCpFormAnyValueId, useCpFormModifiedAnyId } from "../gvc/testNewForm/utils";
import { useCpFormContext } from "../../components/form/useCpFormContext";
import { Navigate, Route, Routes } from "react-router-dom";
import { BasePathContext, useBasePath } from "../../reactContexts/basePathContext";
import { useAppDispatch } from "../../store/hooks";
import { IS_DEPLOYMENT_ENV_PROD, IS_LOCAL } from "../../envVariables";
import { ContentLayout } from "../../components/layout/contentLayout";
import { SingleNavMenu } from "../../components/antd/layout/singleNavMenu";
import { formActions } from "../../store/formSlice";
import { AccountDetailHeader } from "../workload/accountHeader";
import { BillingAccount, BillingAccountAlert, BillingUserRole } from "../../schema/types/account";
import { BillingUserInput } from "./create/input";
import { Summary } from "./summary";
import { AccountDetailSetupTou } from "./setup/tou";
import { AccountDetailSetupPaymentMethod } from "./setup/paymentMethod";
import { AccountDetailSetupCreateOrg } from "./setup/createOrg";
import { Address } from "./address";
import { Alert } from "./alert";
import { PaymentMethods } from "./paymentMethods";
import { Users } from "./users";
import { AuditTrail } from "../billing/account/auditTrail";
import { AccountOrgs } from "./orgs";
import { Invoices } from "./invoices";
import { UsageOrgNamesProvider } from "../usage-ng/orgNamesProvider";
import { CpRootError } from "../gvc/testNewForm/CpRootError";

interface Props {
  account: BillingAccount;
  tou: boolean;
  users: BillingUserInput[];
  roles: BillingUserRole[];
  alert: BillingAccountAlert;
  orgNames: string[];
  hasOrg: boolean;
  fetchPaymentMethods: any;
  fetchAccount: any;
}
export const AccountDetailContent: React.FC<Props> = ({
  account,
  tou,
  users,
  roles,
  alert,
  orgNames,
  hasOrg,
  fetchPaymentMethods,
  fetchAccount,
}) => {
  const dispatch = useAppDispatch();
  const basePath = useBasePath("/account/:id/*", true, "home");

  const { id: formId, submit } = useCpFormContext();

  const submitting = useCpFormAnyValueId(formId, `meta.submitting`);
  const anyModified = useCpFormModifiedAnyId(formId);

  const setupPage: "none" | "tou" | "org" | "paymentMethod" = React.useMemo(() => {
    if (!tou) {
      return "tou";
    }
    if (account.defaultPaymentMethod !== "ok") {
      return "paymentMethod";
    }
    if (!hasOrg) {
      return "org";
    }
    return "none";
  }, [tou, account.defaultPaymentMethod, hasOrg]);

  const links = React.useMemo(() => {
    const links: SingleNavMenuLink[] = [];

    if (setupPage !== "none") {
      links.push(toLinkPage("Setup", "/setup"));
    }

    links.push(
      toLinkPage("Summary", "/summary", { names: ["account"], showDirty: true }),
      toLinkPage("Address", "/address", { names: ["address"], showDirty: true }),
      toLinkPage("Alert", "/alert", { names: ["alert"], showDirty: true }),
      toLinkPage("Payment Methods", "/payment-methods"),
      toLinkPage("Users", "/users", { names: ["users"], showDirty: true }),
      toLinkDivider(),
      toLinkPage("Audit Trail", "/audit-trail"),
      toLinkPage("Cost & Usage", "/usage"),
      toLinkPage("Invoices", "/invoices"),
      toLinkPage("Orgs", "/orgs"),
    );

    if (anyModified) {
      links.push(
        toLinkSpace(),
        toLinkButton("Reset", {
          onClick: () => {
            dispatch(formActions.reset(formId));
          },
          type: "default",
          disabled: submitting,
        }),
        toLinkButton("Preview", {
          onClick: () => {
            formSet(formId, `meta.previewing`, true);
          },
          type: "default",
          disabled: submitting,
        }),
        toLinkButton("Update", {
          onClick: async () => {
            await submit();
          },
          type: "primary",
          className: "action",
          disabled: submitting,
          loading: submitting,
        }),
      );
    }

    if (!IS_DEPLOYMENT_ENV_PROD || IS_LOCAL) {
      if (!anyModified) {
        links.push(toLinkSpace());
      }
      links.push(
        toLinkButton("Debug", {
          type: "default",
          onClick: () => formSet(formId, `meta.debugging`, true),
          disabled: submitting,
        }),
      );
    }

    return links;
  }, [setupPage, anyModified, submitting]);

  return (
    <BasePathContext.Provider value={basePath}>
      <AccountDetailHeader account={account} roles={roles} />
      <ContentLayout
        nav={<SingleNavMenu useDetailHeader={true} links={links} />}
        content={
          <div className="cpln-cards-container">
            <CpRootError />
            <Routes>
              {setupPage === "none" ? null : (
                <Route
                  path={`setup/*`}
                  element={
                    <div className="cpln-cards-container flex flex-col gap-4 relative">
                      {setupPage === "tou" ? (
                        <AccountDetailSetupTou account={account} />
                      ) : setupPage === "paymentMethod" ? (
                        <AccountDetailSetupPaymentMethod account={account} />
                      ) : setupPage === "org" ? (
                        <AccountDetailSetupCreateOrg account={account} roles={roles} />
                      ) : null}
                    </div>
                  }
                />
              )}
              <Route path={`summary/*`} element={<Summary account={account} />} />
              <Route path={`address/*`} element={<Address account={account} />} />
              <Route path={`alert/*`} element={<Alert alert={alert} />} />
              <Route
                path={`payment-methods/*`}
                element={<PaymentMethods account={account} roles={roles} fetchPaymentMethods={fetchPaymentMethods} />}
              />
              <Route path={`users/*`} element={<Users account={account} users={users} roles={roles} />} />
              <Route path={`audit-trail`} element={<AuditTrail account={account} />} />
              <Route path={`usage`} element={<UsageOrgNamesProvider />} />
              <Route path={`invoices`} element={<Invoices account={account} roles={roles} />} />
              <Route path={`orgs`} element={<AccountOrgs account={account} orgNames={orgNames} />} />
              <Route
                path={`*`}
                element={<Navigate to={`${basePath}/${setupPage === "none" ? "summary" : "setup"}`} replace />}
              />
            </Routes>
          </div>
        }
      />
    </BasePathContext.Provider>
  );
};
